import React, { Component } from "react";
import ReactDatatable from "@ashvin27/react-datatable";
import { Container, Tab, Modal, InputGroup, Form } from "react-bootstrap";
import SimpleReactValidator from "simple-react-validator";
import DatePicker from "react-datepicker";
import Moment from "moment";
import AllPropertyReportSidebar from "../common/AllPropertyReportSidebar";
import WHPayoutService from "../../services/WHPayoutService";
import ReportSidebar from "../common/ReportSidebar";
import MultiImageService from "../../services/MultiImageService";
import UnitsService from "../../services/UnitsService";
import PropertyService from "../../services/PropertyService";
import Select from "react-select";
import { SHARED_COLUMN_FORMULAS, withFormulaTooltip } from "./formulaTooltip";
import {
  saveReportFilter,
  loadReportFilter,
  clearReportFilter,
  buildRestoredFilterState,
  buildDefaultCheckoutDateQuery,
} from "../../utils/reportSharedFilter";

// Formats currency: adds thousand commas, strips trailing ".00", keeps real decimals.
// e.g. 7000 -> $7,000 | 14500.95 -> $14,500.95 | 10.00 -> $10 | 15.17 -> $15.17
const formatMoney = (value, fallback = "$0") => {
  const num = parseFloat(value);
  if (value === null || value === undefined || value === "" || isNaN(num)) {
    return fallback;
  }
  const formatted = num.toFixed(2).replace(/\.00$/, "");
  const parts = formatted.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return "$" + parts.join(".");
};

// The underlying react-datatable library renders a flat column header row —
// it has no colSpan/grouped-header support. Each column's header renders its
// own small group label above the column title instead; matching background
// color on adjacent group columns reads visually as one continuous group band.
// Uses <div> (block-level by native HTML, not dependent on any stylesheet
// loading) rather than <span> so the group label always stacks on its own
// line above the title, even if custom.scss hasn't been rebuilt/redeployed
// in a given environment yet.
const groupedHeader = (groupClass, groupLabel, title) => (
  <div className="wh-group-header">
    {/* <div className={"wh-group-label " + groupClass}>{groupLabel}</div> */}
    <div className="wh-group-title">{title}</div>
  </div>
);

// Formula-text BUILDERS shown in each column header's info-icon tooltip - kept in sync
// column-by-column with PayOutReport::whRevenueShareReport() (app/Models/PayOutReport.php).
// Each entry is a function of (startDateLabel, endDateLabel) - the report's currently
// selected Period Start Date / Period End Date, already formatted (or a fallback phrase
// when no date is selected) - so the two date-bounded columns always show the actual
// searched dates instead of a placeholder. Per-unit values (payout percentages, FF&E
// dates) can't be resolved to one number at the column-header level since every row is a
// different unit with its own configured values, so those are described with the real
// field name plus the same "if set" conditional the SQL itself branches on, per column.
const COLUMN_FORMULAS = {
  // wh_earning_itd/period and intro_earning_itd/period are overridden locally below
  // so this report uses formula-notation style while SHARED_COLUMN_FORMULAS stays as prose
  // for other reports that haven't been updated yet.
  ...SHARED_COLUMN_FORMULAS,
  nightly_revenue_itd: () =>
    "Σ Total Nightly Rate (checked out ≤ today, from FF&E Paydown Start Date if set) + Long-Term Stay Revenue (ITD)",
  nightly_revenue_period: (startDateLabel, endDateLabel) =>
    `Σ Total Nightly Rate (checked out between ${startDateLabel} and ${endDateLabel}, from FF&E Paydown Start Date if set) + Long-Term Stay Revenue (same period)`,
  nightly_revenue_future: () =>
    "Σ Total Nightly Rate (checking out after today) + Long-Term Stay Revenue (future portion)",
  voucher_discount_itd: () =>
    "Σ Voucher Discount — reservations checked out ≤ today, from FF&E Paydown Start Date if set",
  voucher_discount_period: (startDateLabel, endDateLabel) =>
    `Σ Voucher Discount — reservations checked out between ${startDateLabel} and ${endDateLabel}, from FF&E Paydown Start Date if set`,
  wh_earning_itd: () =>
    "Σ (Total Nightly Rate × FF&E Brokerage Fee %) — effective range: from FF&E Brokerage Fee Start Date (if set) through FF&E Brokerage Fee End Date (if set). Records outside this range are excluded.",
  wh_earning_period: (startDateLabel, endDateLabel) =>
    `Σ (Total Nightly Rate × FF&E Brokerage Fee %) — effective range: max(${startDateLabel}, FF&E Start Date) through min(${endDateLabel}, FF&E End Date). If Period Start Date is before FF&E Start Date, records begin from FF&E Start Date. If Period End Date is after FF&E End Date, records stop at FF&E End Date.`,
  intro_earning_itd: () =>
    "Σ (Total Nightly Rate × Introductory Brokerage Fee %) — effective range: from Introductory Brokerage Fee Start Date (if set) through Introductory Brokerage Fee End Date (if set). Records outside this range are excluded.",
  intro_earning_period: (startDateLabel, endDateLabel) =>
    `Σ (Total Nightly Rate × Introductory Brokerage Fee %) — effective range: max(${startDateLabel}, Introductory Start Date) through min(${endDateLabel}, Introductory End Date). If Period Start Date is before Introductory Start Date, records begin from Introductory Start Date. If Period End Date is after Introductory End Date, records stop at Introductory End Date.`,
  payout_wh: () =>
    "FF&E Brokerage Fee % — set per unit in Property Earning Configuration",
  intro_percentage: () =>
    "Introductory Brokerage Fee % — set per unit in Property Earning Configuration",
  owner_percentage: () =>
    "Property Owner Payout % — set per unit in Property Earning Configuration",
  property_earning_itd: () =>
    "Σ ((Total Nightly Rate × Property Payout %) + (Cleaning Fee × Property Cleaning Fee %) − (Channel Fee × Property Channel Fee %) + Property Resolution Fee, from FF&E Paydown Start Date if set) − FF&E Basis (minimum $0)",
  property_earning_period: (startDateLabel, endDateLabel) =>
    `Σ ((Total Nightly Rate × Property Payout %) + (Cleaning Fee × Property Cleaning Fee %) − (Channel Fee × Property Channel Fee %) + Property Resolution Fee, checked out between ${startDateLabel} and ${endDateLabel}, from FF&E Paydown Start Date if set) − FF&E Basis (minimum $0)`,
  resolution_fee_property_itd: () =>
    "Σ Property's share of Resolution Fees — checked out ≤ today, from FF&E Paydown Start Date if set",
  resolution_fee_property_period: (startDateLabel, endDateLabel) =>
    `Σ Property's share of Resolution Fees — checked out between ${startDateLabel} and ${endDateLabel}, from FF&E Paydown Start Date if set`,
  ffe_basis: () =>
    "Contracted FF&E cost to recoup — set in Unit FF&E Configuration",
  ffe_paid_down: () =>
    "Σ (Total Property Earning + Property Resolution Fee, from FF&E Paydown Start Date through today) — capped at FF&E Basis",
  ffe_paid_down_period: (startDateLabel, endDateLabel) =>
    `FF&E Paid Down as of ${endDateLabel} − FF&E Paid Down as of day before ${startDateLabel} (each amount capped at FF&E Basis before subtracting)`,
  ffe_balance_remaining: () => "FF&E Basis − FF&E Paid Down ITD (minimum $0)",
  ffe_percent_recouped: () =>
    "(FF&E Paid Down ITD ÷ FF&E Basis) × 100 — capped at 100%",
  resident_percentage: () =>
    "Resident Payout % — set per unit in Property Earning Configuration",
  resident_earning_itd: () =>
    "Σ ((Total Nightly Rate × Resident Payout %) + (Cleaning Fee × Resident Cleaning Fee %) − (Channel Fee × Resident Channel Fee %)) — all reservations, no date limit",
  resident_earning_period: (startDateLabel, endDateLabel) =>
    `Σ ((Total Nightly Rate × Resident Payout %) + (Cleaning Fee × Resident Cleaning Fee %) − (Channel Fee × Resident Channel Fee %)) — reservations checked out between ${startDateLabel} and ${endDateLabel}`,
};

// Shipped defaults — must stay in sync with the seed rows in the
// wh_revenue_share_report_labels migration. Used until the backend labels
// endpoint responds, and as the "Reset" value in the Edit Labels modal.
const DEFAULT_LABELS = {
  report_title: "Revenue Share Report",
  group_ffe_marketing_fee: "FF&E Marketing Fee",
  group_introductory_fee: "Introductory Fee",
  group_resident_payout: "Resident Payout",
  property_name: "Property Name",
  unit_name: "Unit Number",
  floor_plan_type: "Floor Plan",
  nightly_revenue_itd: "ITD Nightly Revenue Earned",
  nightly_revenue_period: "Nightly Revenue Earned (Reporting Period)",
  nightly_revenue_future: "Future Nightly Revenue",
  voucher_discount_itd: "Voucher Discount ITD",
  voucher_discount_period: "Voucher Discount (Reporting Period)",
  creditor: "Creditor",
  wh_start_date: "FF&E Brokerage Fee Start Date",
  wh_end_date: "FF&E Brokerage Fee End Date",
  payout_wh: "FF&E Brokerage Fee %",
  wh_earning_itd: "Cumulative FF&E Brokerage Fee ($)",
  wh_earning_period: "FF&E Brokerage Fee (Reporting Period)",
  intro_start_date: "Introductory Brokerage Fee Start Date",
  intro_end_date: "Introductory Brokerage Fee End Date",
  intro_percentage: "Introductory Brokerage Fee %",
  intro_earning_itd: "Cumulative Introductory Fee ($)",
  intro_earning_period: "Introductory Brokerage Fee (Reporting Period)",
  owner_percentage: "Property Owner Payout Percentage",
  property_earning_itd: "Property Owner Payout Cumulative",
  property_earning_period: "Property Owner Payout (Reporting Period)",
  resolution_fee_property_itd: "Resolution Fee (Property $) ITD",
  resolution_fee_property_period:
    "Resolution Fee (Property $) (Reporting Period)",
  ffe_basis: "FF&E Basis",
  ffe_paid_down: "FF&E Paid Down ITD",
  ffe_paid_down_period: "FF&E Paid Down (Reporting Period)",
  ffe_balance_remaining: "FF&E Balance Remaining ITD",
  ffe_percent_recouped: "FF&E % Recouped",
  resident_percentage: "Resident Payout Percentage",
  resident_earning_itd: "Resident Payout Cumulative",
  resident_earning_period: "Resident Payout (Reporting Period)",
};

// Extracted (rather than inlined in buildColumns below) so eslint's
// react/display-name heuristic doesn't mistake it for an unnamed component.
const renderFfeBalanceCell = (value) => {
  const balance = parseFloat(value || 0);
  const cls = balance > 0 ? "ffe-balance-positive" : "ffe-balance-zero";
  return <span className={cls}>{formatMoney(balance)}</span>;
};

// Builds the react-datatable columns array from the current (possibly
// admin-edited) label map, so renaming a header never requires a code change.
const buildColumns = (labels, startDateLabel, endDateLabel) => [
  // --- Ungrouped (leading) ---
  {
    key: "property_name",
    text: labels.property_name,
    className: "property_name hand-cursor text-center td_clickable",
    width: 160,
    cell: (record) => {
      if (record.is_total) {
        return <span className="grand_total1 wh-total-label">Grand Total</span>;
      }
      return record.property_name || "";
    },
  },
  {
    key: "unit_name",
    text: labels.unit_name,
    className: "unit_name hand-cursor text-center td_clickable",
    width: 120,
    cell: (record) => {
      if (record.is_total) return "";
      return record.unit_name || "";
    },
  },
  {
    key: "floor_plan_type",
    text: labels.floor_plan_type,
    className: "floor_plan_type hand-cursor text-center td_clickable",
    width: 100,
    cell: (record) => {
      if (record.is_total) return "";
      return record.floor_plan_type || "-";
    },
  },
  {
    key: "nightly_revenue_itd",
    text: withFormulaTooltip(
      labels.nightly_revenue_itd,
      COLUMN_FORMULAS.nightly_revenue_itd(startDateLabel, endDateLabel),
    ),
    className: "nightly_revenue_itd hand-cursor text-center td_clickable",
    width: 170,
    cell: (record) => formatMoney(record.nightly_revenue_itd),
  },
  {
    key: "nightly_revenue_period",
    text: withFormulaTooltip(
      labels.nightly_revenue_period,
      COLUMN_FORMULAS.nightly_revenue_period(startDateLabel, endDateLabel),
    ),
    className: "nightly_revenue_period hand-cursor text-center td_clickable",
    width: 200,
    cell: (record) => formatMoney(record.nightly_revenue_period),
  },
  {
    key: "nightly_revenue_future",
    text: withFormulaTooltip(
      labels.nightly_revenue_future,
      COLUMN_FORMULAS.nightly_revenue_future(startDateLabel, endDateLabel),
    ),
    className: "nightly_revenue_future hand-cursor text-center td_clickable",
    width: 190,
    cell: (record) => formatMoney(record.nightly_revenue_future),
  },
  {
    key: "voucher_discount_itd",
    text: withFormulaTooltip(
      labels.voucher_discount_itd,
      COLUMN_FORMULAS.voucher_discount_itd(startDateLabel, endDateLabel),
    ),
    className: "voucher_discount_itd hand-cursor text-center td_clickable",
    width: 160,
    cell: (record) => formatMoney(record.voucher_discount_itd),
  },
  {
    key: "voucher_discount_period",
    text: withFormulaTooltip(
      labels.voucher_discount_period,
      COLUMN_FORMULAS.voucher_discount_period(startDateLabel, endDateLabel),
    ),
    className: "voucher_discount_period hand-cursor text-center td_clickable",
    width: 200,
    cell: (record) => formatMoney(record.voucher_discount_period),
  },
  {
    key: "creditor",
    text: labels.creditor,
    className: "creditor hand-cursor text-center td_clickable",
    width: 110,
    cell: (record) => {
      if (record.is_total) return "";
      return record.creditor || "-";
    },
  },
  // --- FF&E Marketing Fee group ---
  {
    key: "wh_start_date",
    text: groupedHeader(
      "wh-group-label-ffe",
      labels.group_ffe_marketing_fee,
      labels.wh_start_date,
    ),
    className: "wh_start_date hand-cursor text-center td_clickable",
    width: 140,
    cell: (record) => {
      if (record.is_total) return "";
      return record.wh_start_date
        ? Moment(record.wh_start_date).format(global.dateFormat)
        : "-";
    },
  },
  {
    key: "wh_end_date",
    text: groupedHeader(
      "wh-group-label-ffe",
      labels.group_ffe_marketing_fee,
      labels.wh_end_date,
    ),
    className: "wh_end_date hand-cursor text-center td_clickable",
    width: 140,
    cell: (record) => {
      if (record.is_total) return "";
      return record.wh_end_date
        ? Moment(record.wh_end_date).format(global.dateFormat)
        : "-";
    },
  },
  {
    key: "payout_wh",
    text: groupedHeader(
      "wh-group-label-ffe",
      labels.group_ffe_marketing_fee,
      withFormulaTooltip(
        labels.payout_wh,
        COLUMN_FORMULAS.payout_wh(startDateLabel, endDateLabel),
      ),
    ),
    className: "payout_wh hand-cursor text-center td_clickable",
    width: 130,
    cell: (record) => {
      if (record.is_total) return "";
      return record.wh_percentage ? record.wh_percentage + "%" : "0%";
    },
  },
  {
    key: "wh_earning_itd",
    text: groupedHeader(
      "wh-group-label-ffe",
      labels.group_ffe_marketing_fee,
      withFormulaTooltip(
        labels.wh_earning_itd,
        COLUMN_FORMULAS.wh_earning_itd(startDateLabel, endDateLabel),
      ),
    ),
    className: "wh_earning_itd hand-cursor text-center td_clickable",
    width: 160,
    cell: (record) => formatMoney(record.wh_earning_itd),
  },
  {
    key: "wh_earning_period",
    text: groupedHeader(
      "wh-group-label-ffe",
      labels.group_ffe_marketing_fee,
      withFormulaTooltip(
        labels.wh_earning_period,
        COLUMN_FORMULAS.wh_earning_period(startDateLabel, endDateLabel),
      ),
    ),
    className: "wh_earning_period hand-cursor text-center td_clickable",
    width: 170,
    cell: (record) => formatMoney(record.wh_earning_period),
  },
  // --- Introductory Fee group ---
  {
    key: "intro_start_date",
    text: groupedHeader(
      "wh-group-label-intro",
      labels.group_introductory_fee,
      labels.intro_start_date,
    ),
    className: "intro_start_date hand-cursor text-center td_clickable",
    width: 150,
    cell: (record) => {
      if (record.is_total) return "";
      return record.intro_start_date
        ? Moment(record.intro_start_date).format(global.dateFormat)
        : "-";
    },
  },
  {
    key: "intro_end_date",
    text: groupedHeader(
      "wh-group-label-intro",
      labels.group_introductory_fee,
      labels.intro_end_date,
    ),
    className: "intro_end_date hand-cursor text-center td_clickable",
    width: 150,
    cell: (record) => {
      if (record.is_total) return "";
      return record.intro_end_date
        ? Moment(record.intro_end_date).format(global.dateFormat)
        : "-";
    },
  },
  {
    key: "intro_percentage",
    text: groupedHeader(
      "wh-group-label-intro",
      labels.group_introductory_fee,
      withFormulaTooltip(
        labels.intro_percentage,
        COLUMN_FORMULAS.intro_percentage(startDateLabel, endDateLabel),
      ),
    ),
    className: "intro_percentage hand-cursor text-center td_clickable",
    width: 140,
    cell: (record) => {
      if (record.is_total) return "";
      return record.intro_percentage ? record.intro_percentage + "%" : "0%";
    },
  },
  {
    key: "intro_earning_itd",
    text: groupedHeader(
      "wh-group-label-intro",
      labels.group_introductory_fee,
      withFormulaTooltip(
        labels.intro_earning_itd,
        COLUMN_FORMULAS.intro_earning_itd(startDateLabel, endDateLabel),
      ),
    ),
    className: "intro_earning_itd hand-cursor text-center td_clickable",
    width: 160,
    cell: (record) => formatMoney(record.intro_earning_itd),
  },
  {
    key: "intro_earning_period",
    text: groupedHeader(
      "wh-group-label-intro",
      labels.group_introductory_fee,
      withFormulaTooltip(
        labels.intro_earning_period,
        COLUMN_FORMULAS.intro_earning_period(startDateLabel, endDateLabel),
      ),
    ),
    className: "intro_earning_period hand-cursor text-center td_clickable",
    width: 170,
    cell: (record) => formatMoney(record.intro_earning_period),
  },
  // --- Ungrouped (trailing) ---
  {
    key: "owner_percentage",
    text: withFormulaTooltip(
      labels.owner_percentage,
      COLUMN_FORMULAS.owner_percentage(startDateLabel, endDateLabel),
    ),
    className: "owner_percentage hand-cursor text-center td_clickable",
    width: 160,
    cell: (record) => {
      if (record.is_total) return "";
      return record.owner_percentage ? record.owner_percentage + "%" : "0%";
    },
  },
  {
    key: "property_earning_itd",
    text: withFormulaTooltip(
      labels.property_earning_itd,
      COLUMN_FORMULAS.property_earning_itd(startDateLabel, endDateLabel),
    ),
    className: "property_earning_itd hand-cursor text-center td_clickable",
    width: 200,
    cell: (record) => formatMoney(record.property_earning_itd),
  },
  {
    key: "property_earning_period",
    text: withFormulaTooltip(
      labels.property_earning_period,
      COLUMN_FORMULAS.property_earning_period(startDateLabel, endDateLabel),
    ),
    className: "property_earning_period hand-cursor text-center td_clickable",
    width: 215,
    cell: (record) => formatMoney(record.property_earning_period),
  },
  {
    key: "resolution_fee_property_itd",
    text: withFormulaTooltip(
      labels.resolution_fee_property_itd,
      COLUMN_FORMULAS.resolution_fee_property_itd(startDateLabel, endDateLabel),
    ),
    className:
      "resolution_fee_property_itd hand-cursor text-center td_clickable",
    width: 200,
    cell: (record) => formatMoney(record.resolution_fee_property_itd),
  },
  {
    key: "resolution_fee_property_period",
    text: withFormulaTooltip(
      labels.resolution_fee_property_period,
      COLUMN_FORMULAS.resolution_fee_property_period(
        startDateLabel,
        endDateLabel,
      ),
    ),
    className:
      "resolution_fee_property_period hand-cursor text-center td_clickable",
    width: 220,
    cell: (record) => formatMoney(record.resolution_fee_property_period),
  },
  {
    key: "ffe_basis",
    text: withFormulaTooltip(
      labels.ffe_basis,
      COLUMN_FORMULAS.ffe_basis(startDateLabel, endDateLabel),
    ),
    className: "ffe_basis hand-cursor text-center td_clickable",
    width: 130,
    cell: (record) => {
      if (record.is_total) return formatMoney(record.ffe_basis_total);
      return formatMoney(record.ffe_basis, "-");
    },
  },
  {
    key: "ffe_paid_down",
    text: withFormulaTooltip(
      labels.ffe_paid_down,
      COLUMN_FORMULAS.ffe_paid_down(startDateLabel, endDateLabel),
    ),
    className: "ffe_paid_down hand-cursor text-center td_clickable",
    width: 160,
    cell: (record) =>
      formatMoney(
        record.is_total ? record.ffe_paid_down_total : record.ffe_paid_down,
      ),
  },
  {
    key: "ffe_paid_down_period",
    text: withFormulaTooltip(
      labels.ffe_paid_down_period,
      COLUMN_FORMULAS.ffe_paid_down_period(startDateLabel, endDateLabel),
    ),
    className: "ffe_paid_down_period hand-cursor text-center td_clickable",
    width: 200,
    cell: (record) => formatMoney(record.ffe_paid_down_period),
  },
  {
    key: "ffe_balance_remaining",
    text: withFormulaTooltip(
      labels.ffe_balance_remaining,
      COLUMN_FORMULAS.ffe_balance_remaining(startDateLabel, endDateLabel),
    ),
    className: "ffe_balance_remaining hand-cursor text-center td_clickable",
    width: 190,
    cell: (record) =>
      renderFfeBalanceCell(
        record.is_total
          ? record.ffe_balance_total
          : record.ffe_balance_remaining,
      ),
  },
  {
    key: "ffe_percent_recouped",
    text: withFormulaTooltip(
      labels.ffe_percent_recouped,
      COLUMN_FORMULAS.ffe_percent_recouped(startDateLabel, endDateLabel),
    ),
    className: "ffe_percent_recouped hand-cursor text-center td_clickable",
    width: 150,
    cell: (record) => {
      if (record.is_total) return "";
      // NOTE: values above 100% are valid — they indicate FF&E paid down
      // exceeds the original basis (e.g. 118.1% means the property has
      // overpaid its FF&E commitment). Confirm with the business team if
      // a cap is desired; do not silently truncate without that decision.
      return record.ffe_percent_recouped
        ? parseFloat(record.ffe_percent_recouped)
            .toFixed(1)
            .replace(/\.0$/, "") + "%"
        : "0%";
    },
  },
  // --- Resident Payout group ---
  {
    key: "resident_percentage",
    text: groupedHeader(
      "wh-group-label-resident",
      labels.group_resident_payout,
      withFormulaTooltip(
        labels.resident_percentage,
        COLUMN_FORMULAS.resident_percentage(startDateLabel, endDateLabel),
      ),
    ),
    className: "resident_percentage hand-cursor text-center td_clickable",
    width: 150,
    cell: (record) => {
      if (record.is_total) return "";
      return record.resident_percentage
        ? record.resident_percentage + "%"
        : "0%";
    },
  },
  {
    key: "resident_earning_itd",
    text: groupedHeader(
      "wh-group-label-resident",
      labels.group_resident_payout,
      withFormulaTooltip(
        labels.resident_earning_itd,
        COLUMN_FORMULAS.resident_earning_itd(startDateLabel, endDateLabel),
      ),
    ),
    className: "resident_earning_itd hand-cursor text-center td_clickable",
    width: 180,
    cell: (record) => formatMoney(record.resident_earning_itd),
  },
  {
    key: "resident_earning_period",
    text: groupedHeader(
      "wh-group-label-resident",
      labels.group_resident_payout,
      withFormulaTooltip(
        labels.resident_earning_period,
        COLUMN_FORMULAS.resident_earning_period(startDateLabel, endDateLabel),
      ),
    ),
    className: "resident_earning_period hand-cursor text-center td_clickable",
    width: 200,
    cell: (record) => formatMoney(record.resident_earning_period),
  },
];

class WHRevenueShareReport extends Component {
  constructor(props) {
    super(props);
    this.validator = new SimpleReactValidator({ autoForceUpdate: this });
    this.periodStartDateInputGroupRef = React.createRef();
    this.periodEndDateInputGroupRef = React.createRef();
    this.state = {
      periodStartDatePlacement: "bottom-start",
      periodEndDatePlacement: "bottom-start",
      errors: {},
      isSearch: global.isSearch,
      is_search: 0,
      showSpinner: false,
      loading: false,
      total_record: 0,
      records: [],
      totals: {},
      start_date: null,
      end_date: null,
      property_slug: "",
      unitList: [],
      newUnitList: [],
      selectedUnitList: [],
      unit_slug: "",
      unit_lable: "",
      newPropertyList: [],
      selectedPropertyList: [],
      modal_property_slugs: [],
      modal_property_labels: [],
      outSidePropertySlug: "",
      module_search_display: [],
      // Editable header labels (column/group text) — defaults until the
      // labels endpoint responds, then whatever the admin has customized.
      labels: DEFAULT_LABELS,
      editLabels: {},
      isLabelsModalOpen: false,
      isLabelsSaving: false,
      // Export modal (xlsx/csv)
      isExportModalOpen: false,
      exportFileType: "xlsx",
      isExporting: false,
      exportMessage: "",
      config: {
        sort: { column: "", order: "" },
        key_column: "unit_slug",
        page_size: global.page_size,
        length_menu: global.page_length_menu,
        show_length_menu: true,
        show_filter: false,
        show_pagination: false,
        pagination: "advance",
        button: {
          excel: global.excel_button,
          print: global.print_button,
          extra: false,
        },
        language: { no_data_text: "No data found" },
      },
      extraButtons: [],
      columns: buildColumns(
        DEFAULT_LABELS,
        "the selected Period Start Date",
        "the selected Period End Date",
      ),
    };
    this.changeHandler = this.changeHandler.bind(this);
    this.clearFilter = this.clearFilter.bind(this);
    this.addTotalClass = this.addTotalClass.bind(this);
    // Preserves active filter params across pagination/sort changes
    this._filterQueryString = "";
    // Guards for the single initial load - see fireInitialLoadWhenReady()
    this._datesReady = false;
    this._propertyListReady = false;
    this._initialLoadDone = false;
  }

  componentDidMount() {
    this._restoredFilter = loadReportFilter();
    if (this.props.match.params.propertySlug !== undefined) {
      this.setState({ property_slug: this.props.match.params.propertySlug });
    }
    this.getPropertyData();
    this.gerDefaultInOutDate();
    this.fetchLabels();
  }

  // Defaults the period filter to the current month and fetches that
  // month's data by default (mirrors Resident Payout Report's date default)
  gerDefaultInOutDate = () => {
    var date = new Date();
    var firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    var lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    var restored = buildRestoredFilterState(
      this._restoredFilter,
      firstDay,
      lastDay,
    );
    if (restored) {
      // Rebuild module_search_display with this report's own labels so filter chips
      // always read "Period Start Date / Period End Date" regardless of which report
      // originally saved the filter.
      var restoredSearchDisplay = [];
      if (restored.start_date) {
        restoredSearchDisplay.push(
          "Period Start Date - " +
            Moment(restored.start_date).format("MM/DD/YY"),
        );
      }
      if (restored.end_date) {
        restoredSearchDisplay.push(
          "Period End Date - " + Moment(restored.end_date).format("MM/DD/YY"),
        );
      }
      if (restored.unit_lable && restored.unit_lable.length > 0) {
        var unitLabels = Array.isArray(restored.unit_lable)
          ? restored.unit_lable.join(", ")
          : restored.unit_lable;
        restoredSearchDisplay.push("Unit - " + unitLabels);
      }
      restored.module_search_display = restoredSearchDisplay;
      this.setState(restored, () => {
        if (restored.is_search) {
          this._filterQueryString =
            this.buildDateQueryString() + "&is_search=1";
        }
        this.rebuildColumns();
        this._datesReady = true;
        this.fireInitialLoadWhenReady();
      });
    } else {
      this.setState({ start_date: firstDay, end_date: lastDay }, () => {
        this.rebuildColumns();
        this._datesReady = true;
        this.fireInitialLoadWhenReady();
      });
    }
  };

  // Formats the currently-selected Period Start/End Date for use inside a tooltip
  // formula sentence (e.g. "09/01/2026"), with a plain-English fallback when no date
  // has been picked yet — same MM/DD/YYYY-style formatting the rest of this component
  // already uses for these two fields, just via Moment instead of the raw Date object.
  getFormulaDateLabels = () => ({
    startDateLabel: this.state.start_date
      ? Moment(this.state.start_date).format("MM/DD/YYYY")
      : "the selected Period Start Date",
    endDateLabel: this.state.end_date
      ? Moment(this.state.end_date).format("MM/DD/YYYY")
      : "the selected Period End Date",
  });

  // Rebuilds the columns array so header tooltip formulas pick up the current Period
  // Start/End Date — must be called any time start_date/end_date changes (search,
  // clear, initial default load), not just when labels change.
  rebuildColumns = () => {
    const { startDateLabel, endDateLabel } = this.getFormulaDateLabels();
    this.setState({
      columns: buildColumns(
        this.state.labels || DEFAULT_LABELS,
        startDateLabel,
        endDateLabel,
      ),
    });
  };

  // The one place that decides which property_slug this page is allowed to ask for.
  // Property-level page: always the slug from the route, so another property's data
  // can never be requested. Global page: only the slugs picked in the Property
  // dropdown, and an empty value when "All Properties" is selected.
  getScopedPropertySlug = () => {
    if (this.props.match.params.propertySlug !== undefined) {
      return this.state.property_slug
        ? this.state.property_slug
        : this.props.match.params.propertySlug;
    }
    return this.state.modal_property_slugs &&
      this.state.modal_property_slugs.length > 0
      ? this.state.modal_property_slugs.join(",")
      : "";
  };

  // Forces property_slug in a query string to this page's own scope. A query string
  // can come from a saved filter that a different report page (or a different
  // property) wrote, so its property_slug cannot be trusted. It can also be missing
  // altogether, for example on sort/pagination, and the API reads a missing
  // property_slug as "no property filter" and answers with every assigned property.
  applyPropertyScope = (queryString) => {
    const parts = (queryString || "")
      .split("&")
      .filter((part) => part && part.indexOf("property_slug=") !== 0);
    parts.unshift("property_slug=" + this.getScopedPropertySlug());
    return parts.join("&");
  };

  // Fires the first report load once, and only after BOTH the default date range and
  // the Property dropdown have resolved. Previously the date default fired its own
  // request straight away, before the dropdown had loaded, so the global page sent one
  // unscoped request (blank property_slug = every property) and then a second, correctly
  // scoped one.
  fireInitialLoadWhenReady = () => {
    if (this._initialLoadDone) return;
    // A property-level page gets its scope from the route, so it never waits
    // for the dropdown.
    const waitsForPropertyList =
      this.props.match.params.propertySlug === undefined;
    if (!this._datesReady) return;
    if (waitsForPropertyList && !this._propertyListReady) return;
    this._initialLoadDone = true;
    this.getWHRevenueShareReport(this.buildDateQueryString());
  };

  buildDateQueryString = () => {
    let startDate = this.state.start_date
      ? Moment(this.state.start_date).format("YYYY-MM-DD")
      : "";
    let endDate = this.state.end_date
      ? Moment(this.state.end_date).format("YYYY-MM-DD")
      : "";
    let unitSearch = Array.isArray(this.state.unit_slug)
      ? this.state.unit_slug.join(",")
      : this.state.unit_slug || "";
    const propertySlug = this.getScopedPropertySlug();
    return (
      "property_slug=" +
      propertySlug +
      "&unit_slug=" +
      unitSearch +
      "&start_date=" +
      startDate +
      "&end_date=" +
      endDate
    );
  };

  getPropertySlug = (propertySlug) => {
    this.setState(
      {
        property_slug: propertySlug,
        unit_slug: "",
        unit_lable: "",
        selectedUnitList: [],
        is_search: 0,
        module_search_display: [],
      },
      () => {
        this.getWHRevenueShareReport(this.buildDateQueryString());
      },
    );
  };

  getWHRevenueShareReport = async (queryString = "") => {
    this.setState({ showSpinner: true, loading: true });
    if (!queryString) {
      queryString = buildDefaultCheckoutDateQuery(
        this.state.start_date,
        this.state.end_date,
      );
    } else {
      const params = new URLSearchParams(queryString);
      // Clear calls this with no query string, and a restored filter can carry a
      // blank date, so an absent value must fall back to the current month instead
      // of being sent as "start_date=&end_date=" (which the API reads as no range).
      const today = new Date();
      const dateFrom =
        params.get("checkout_date_from") ||
        params.get("start_date") ||
        Moment(new Date(today.getFullYear(), today.getMonth(), 1)).format(
          "YYYY-MM-DD",
        );
      const dateTo =
        params.get("checkout_date_to") ||
        params.get("end_date") ||
        Moment(new Date(today.getFullYear(), today.getMonth() + 1, 0)).format(
          "YYYY-MM-DD",
        );
      params.set("start_date", dateFrom);
      params.set("end_date", dateTo);
      queryString = params.toString();
    }

    var list = [];
    var totalRecords = 0;
    var totals = {};

    let addditionalHeaderData = {};
    let res = await WHPayoutService.getWHRevenueShareReport(
      addditionalHeaderData,
      queryString ? queryString : "",
    );
    if (global.successStatus.includes(res.status)) {
      list = res.data && res.data.data ? res.data.data : [];
      totals = res.data && res.data.totals ? res.data.totals : {};
      totalRecords = list.length;

      // Append a synthetic grand-total row so it renders inside the table.
      // totalRecords intentionally excludes it so pagination counts stay correct.
      if (Object.keys(totals).length > 0) {
        list = [
          ...list,
          {
            is_total: true,
            unit_slug: "__grand_total__",
            nightly_revenue_itd: totals.nightly_revenue_itd || 0,
            nightly_revenue_period: totals.nightly_revenue_period || 0,
            nightly_revenue_future: totals.nightly_revenue_future_total || 0,
            voucher_discount_itd: totals.voucher_discount_itd_total || 0,
            voucher_discount_period: totals.voucher_discount_period_total || 0,
            wh_earning_itd: totals.wh_earning_itd || 0,
            wh_earning_period: totals.wh_earning_period || 0,
            intro_earning_itd: totals.intro_earning_itd_total || 0,
            intro_earning_period: totals.intro_earning_period_total || 0,
            property_earning_itd: totals.property_earning_itd || 0,
            property_earning_period: totals.property_earning_period || 0,
            resolution_fee_property_itd:
              totals.resolution_fee_property_itd_total || 0,
            resolution_fee_property_period:
              totals.resolution_fee_property_period_total || 0,
            ffe_paid_down_period: totals.ffe_paid_down_period_total || 0,
            ffe_basis_total: totals.ffe_basis_total || 0,
            ffe_paid_down_total: totals.ffe_paid_down_total || 0,
            ffe_balance_total: totals.ffe_balance_total || 0,
            resident_earning_itd: totals.resident_earning_itd_total || 0,
            resident_earning_period: totals.resident_earning_period_total || 0,
          },
        ];
      }
    }
    this.setState(
      {
        showSpinner: false,
        loading: false,
        total_record: totalRecords,
        records: list,
        totals: totals,
      },
      this.addTotalClass,
    );
  };

  // Fetches the editable column/group header labels and rebuilds the
  // columns array with them. Falls back to DEFAULT_LABELS on any failure —
  // the report must still render even if the labels endpoint is unavailable.
  fetchLabels = async () => {
    let res = await WHPayoutService.getWHRevenueShareReportLabels();
    if (global.successStatus.includes(res.status)) {
      let rows = res.data && res.data.data ? res.data.data : [];
      let labels = { ...DEFAULT_LABELS };
      rows.forEach((row) => {
        labels[row.column_key] = row.effective_label;
      });
      const { startDateLabel, endDateLabel } = this.getFormulaDateLabels();
      this.setState({
        labels: labels,
        columns: buildColumns(labels, startDateLabel, endDateLabel),
      });
    }
  };

  openLabelsModal = () => {
    // Seed the editable copy from the live labels so unsaved edits don't
    // leak into the report until "Save" is clicked.
    this.setState({
      editLabels: { ...this.state.labels },
      isLabelsModalOpen: true,
    });
  };

  closeLabelsModal = () => {
    this.setState({ isLabelsModalOpen: false });
  };

  changeLabelInput = (columnKey, value) => {
    this.setState({
      editLabels: { ...this.state.editLabels, [columnKey]: value },
    });
  };

  resetLabelInput = (columnKey) => {
    this.setState({
      editLabels: {
        ...this.state.editLabels,
        [columnKey]: DEFAULT_LABELS[columnKey],
      },
    });
  };

  saveLabels = async () => {
    this.setState({ isLabelsSaving: true });
    let labelsPayload = Object.keys(DEFAULT_LABELS).map((columnKey) => ({
      column_key: columnKey,
      // Sending back the default text resets it server-side (no override stored).
      custom_label:
        this.state.editLabels[columnKey] === DEFAULT_LABELS[columnKey]
          ? ""
          : this.state.editLabels[columnKey],
    }));

    let res = await WHPayoutService.updateWHRevenueShareReportLabels({
      labels: labelsPayload,
    });
    if (global.successStatus.includes(res.status)) {
      let rows = res.data && res.data.data ? res.data.data : [];
      let labels = { ...DEFAULT_LABELS };
      rows.forEach((row) => {
        labels[row.column_key] = row.effective_label;
      });
      const { startDateLabel, endDateLabel } = this.getFormulaDateLabels();
      this.setState({
        labels: labels,
        columns: buildColumns(labels, startDateLabel, endDateLabel),
        isLabelsModalOpen: false,
      });
    }
    this.setState({ isLabelsSaving: false });
  };

  exportToggle = () => {
    this.setState({ isExportModalOpen: !this.state.isExportModalOpen });
  };

  changeExportFileType = (event) => {
    this.setState({ exportFileType: event.target.value });
  };

  exportReport = async () => {
    this.setState({ isExporting: true });
    let queryString =
      this.buildDateQueryString() +
      "&is_export=1" +
      "&file_type=" +
      this.state.exportFileType;

    let res = await WHPayoutService.exportWHRevenueShareReport(queryString);
    if (
      global.successStatus.includes(res.status) &&
      res.data &&
      res.data.filePath
    ) {
      this.setState({ exportMessage: res.message });
      await MultiImageService.downloadFile(
        {},
        "path=" +
          res.data.filePath +
          "&display_file_name=" +
          res.data.fileName.split(".")[0],
      );
    } else {
      this.setState({
        exportMessage: res.message || "No data found for the selected filters.",
      });
    }
    this.setState({ isExporting: false });
  };

  searchToggle = () => {
    if (this.state.isSearch === 1) {
      this.setState({ isSearch: 0 });
    } else {
      this.getUnitData();
      this.setState({ isSearch: 1 });
    }
  };

  getPropertyData = async () => {
    let res = await PropertyService.getPropertyList("is_global_dropdown=1");
    if (global.successStatus.includes(res.status)) {
      const list = res.data ? res.data : [];
      const mapped = list.map(({ slug, property_name }) => ({
        value: slug,
        label: property_name,
      }));
      const newPropertyList = [
        { value: "", label: "All Properties" },
        ...mapped,
      ];
      // Restore persisted multi-selection from previous report navigation.
      // Fall back to the single slug saved by listing-module OutSidePropertySelectBox.
      // Only applies on the global (no propertySlug route param) report page —
      // property-level pages must always start with an empty selection so the
      // route's own property_slug is used, not a stale global selection.
      let preSelectedSlugs = [];
      if (this.props.match.params.propertySlug === undefined) {
        try {
          const saved = localStorage.getItem("reportSelectedPropertySlugs");
          if (saved) preSelectedSlugs = JSON.parse(saved);
        } catch (e) {
          /* ignore malformed localStorage value */
        }
        if (!preSelectedSlugs.length) {
          const single = localStorage.getItem("outSidePorpertySlug");
          if (single) preSelectedSlugs = [single];
        }
      }
      const resolvedSelection = preSelectedSlugs
        .map((slug) => mapped.find((p) => p.value === slug))
        .filter(Boolean);
      // No saved selection — default to the "All Properties" chip, matching OutSidePropertySelectBox's default.
      const selectedPropertyList = resolvedSelection.length
        ? resolvedSelection
        : [{ value: "", label: "All Properties" }];
      const modal_property_slugs = selectedPropertyList
        .filter((p) => p.value)
        .map((p) => p.value);
      this.setState(
        { newPropertyList, selectedPropertyList, modal_property_slugs },
        () => {
          this._propertyListReady = true;
          if (this.props.match.params.propertySlug === undefined) {
            this.getUnitData();
          }
          this.fireInitialLoadWhenReady();
        },
      );
    } else {
      // Property list could not be loaded. Do not leave the global page stuck with
      // no data - let the report load with whatever scope is already in state.
      this._propertyListReady = true;
      this.fireInitialLoadWhenReady();
    }
  };

  getUnitData = async () => {
    const propertySlugParam = this.getScopedPropertySlug();
    let res = await UnitsService.getUnitsList(
      "is_dropdown=1&property_slug=" + propertySlugParam,
    );
    if (global.successStatus.includes(res.status)) {
      const unitListData = res.data ? res.data : [];
      const newUnitList =
        unitListData.length > 0
          ? unitListData.map(({ slug, unit_name }) => ({
              value: slug,
              label: unit_name,
            }))
          : [];
      this.setState({ unitList: unitListData, newUnitList: newUnitList });
    }
  };

  handleChangeUnitList = (value) => {
    let unit_slug = [];
    let unit_lable = [];
    if (value != null && value.length > 0) {
      value.forEach((item) => {
        unit_slug.push(item.value);
        unit_lable.push(item.label);
      });
    }
    this.setState({
      selectedUnitList: value,
      unit_slug: unit_slug,
      unit_lable: unit_lable,
    });
  };

  handleChangePropertyList = (value) => {
    const values = value || [];
    const specificSelections = values.filter((v) => v.value !== "");
    // "All Properties" chosen, or every chip cleared — default back to the "All Properties" chip
    if (specificSelections.length === 0) {
      localStorage.removeItem("reportSelectedPropertySlugs");
      this.setState(
        {
          selectedPropertyList: [{ value: "", label: "All Properties" }],
          modal_property_slugs: [],
          modal_property_labels: [],
          selectedUnitList: [],
          unit_slug: "",
          unit_lable: "",
        },
        () => {
          this.getUnitData();
          this.getWHRevenueShareReport(this.buildDateQueryString());
        },
      );
      return;
    }
    let slugs = specificSelections.map((item) => item.value);
    let labels = specificSelections.map((item) => item.label);
    localStorage.setItem("reportSelectedPropertySlugs", JSON.stringify(slugs));
    this.setState(
      {
        selectedPropertyList: specificSelections,
        modal_property_slugs: slugs,
        modal_property_labels: labels,
        selectedUnitList: [],
        unit_slug: "",
        unit_lable: "",
      },
      () => {
        this.getUnitData();
        this.getWHRevenueShareReport(this.buildDateQueryString());
      },
    );
  };

  clearFilter = async () => {
    var date = new Date();
    var firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    var lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    this._filterQueryString = "";
    clearReportFilter();
    this._restoredFilter = null;
    // On the property-level page the route itself scopes the report to one
    // property — clearing must keep that scope, not blank it out (blanking
    // it made buildDateQueryString() send an empty property_slug, which
    // fetches every property's records instead of just this one).
    const clearedPropertySlug =
      this.props.match.params.propertySlug !== undefined
        ? this.props.match.params.propertySlug
        : "";
    // On the global page, Clear resets the date/unit filters only — the
    // property dropdown selection made via the Property select box stays
    // as-is, it is not part of the "Search" modal's filters being cleared.
    this.setState(
      {
        is_search: 0,
        start_date: firstDay,
        end_date: lastDay,
        property_slug: clearedPropertySlug,
        unit_slug: "",
        unit_lable: "",
        selectedUnitList: [],
        module_search_display: [],
      },
      () => {
        this.rebuildColumns();
        this.getWHRevenueShareReport(this.buildDateQueryString());
      },
    );
  };

  // Applies grand_total CSS class to the synthetic totals row via DOM traversal.
  // ReactDatatable renders TR outside React's control so we can't use className directly.
  addTotalClass = () => {
    const grandTotal = document.getElementsByClassName("grand_total1");
    if (grandTotal.length > 0) {
      const ele = grandTotal[0].parentElement.parentElement;
      if (ele) {
        ele.classList.add("grand_total");
      }
    }
  };

  getResponsivePlacement = (ref) => {
    if (!ref.current) return "bottom-start";

    const rect = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const CALENDAR_HEIGHT_ESTIMATE = 330;
    const CALENDAR_WIDTH_ESTIMATE = 300;

    if (spaceBelow >= CALENDAR_HEIGHT_ESTIMATE || window.innerWidth <= 767) {
      return "bottom-start";
    }

    const spaceRight = window.innerWidth - rect.right;
    const spaceLeft = rect.left;

    if (spaceRight >= CALENDAR_WIDTH_ESTIMATE) return "right-start";
    if (spaceLeft >= CALENDAR_WIDTH_ESTIMATE) return "left-start";
    return spaceRight >= spaceLeft ? "right-start" : "left-start";
  };

  checkPeriodStartDatePlacement = () => {
    this.setState({
      periodStartDatePlacement: this.getResponsivePlacement(
        this.periodStartDateInputGroupRef,
      ),
    });
  };

  checkPeriodEndDatePlacement = () => {
    this.setState({
      periodEndDatePlacement: this.getResponsivePlacement(
        this.periodEndDateInputGroupRef,
      ),
    });
  };

  changeHandler = async (event) => {
    await this.setState({ [event.target.name]: event.target.value });
  };

  filterData = () => {
    const fromError = !this.state.start_date ? "Start Date is required." : "";
    const toError = !this.state.end_date ? "End Date is required." : "";
    if (fromError || toError) {
      this.setState({ checkoutFromError: fromError, checkoutToError: toError });
      return;
    }
    this.setState({ checkoutFromError: "", checkoutToError: "" });
    let startDate = this.state.start_date
      ? Moment(this.state.start_date).format("YYYY-MM-DD")
      : "";
    let endDate = this.state.end_date
      ? Moment(this.state.end_date).format("YYYY-MM-DD")
      : "";

    let queryString = this.buildDateQueryString() + "&is_search=1";

    let searchDisplay = [];
    if (this.state.unit_lable && this.state.unit_lable.length > 0) {
      const unitLabels = Array.isArray(this.state.unit_lable)
        ? this.state.unit_lable.join(", ")
        : this.state.unit_lable;
      searchDisplay.push("Unit - " + unitLabels);
    }
    if (startDate) {
      searchDisplay.push(
        "Period Start Date - " +
          Moment(this.state.start_date).format("MM/DD/YY"),
      );
    }
    if (endDate) {
      searchDisplay.push(
        "Period End Date - " + Moment(this.state.end_date).format("MM/DD/YY"),
      );
    }

    saveReportFilter({
      start_date: this.state.start_date,
      end_date: this.state.end_date,
      unit_slug: this.state.unit_slug || [],
      unit_lable: this.state.unit_lable || [],
      selectedUnitList: this.state.selectedUnitList || [],
      is_search: 1,
      customQueryString: queryString,
      module_search_display: searchDisplay,
    });
    this._filterQueryString = queryString;
    this.searchToggle();
    this.setState({
      is_search: 1,
      module_search_display: searchDisplay,
    });
    this.rebuildColumns();
    this.getWHRevenueShareReport(queryString);
  };

  tableChangeHandler = (data) => {
    let queryString = Object.keys(data)
      .map((key) => {
        if (key === "sort_order" && data[key]) {
          return (
            encodeURIComponent("sort_order") +
            "=" +
            encodeURIComponent(data[key].order) +
            "&" +
            encodeURIComponent("sort_column") +
            "=" +
            encodeURIComponent(data[key].column)
          );
        } else {
          let newKey = key;
          if (key === "page_number") {
            newKey = "page";
          } else if (key === "page_size") {
            newKey = "per_page";
          } else if (key === "sort_order") {
            data[key] = "asc";
          }
          return (
            encodeURIComponent(newKey) + "=" + encodeURIComponent(data[key])
          );
        }
      })
      .join("&");
    // Preserve the active filter across pagination/sort — without this the date range
    // would be silently dropped when the user changes page or sort order. When no
    // search is active the current date defaults are sent instead, because sending
    // nothing left property_slug off the request entirely and the API then returned
    // every assigned property. applyPropertyScope also re-stamps the property scope,
    // since a saved filter can hold a property_slug from an earlier selection.
    queryString +=
      "&" +
      this.applyPropertyScope(
        this._filterQueryString || this.buildDateQueryString(),
      );
    this.getWHRevenueShareReport(queryString);
  };

  render() {
    // const { totals } = this.state;
    return (
      <>
        <main>
          <Container className="innter-container">
            <div className="d-flex flex-column">
              <div className="row row-xs mg-t-0 pd-t-0">
                {this.props.match.params.propertySlug !== undefined ? (
                  <div className="col-md-2 left">
                    <div className="child-menu-section">
                      <ReportSidebar property_slug={this.state.property_slug} />
                    </div>
                  </div>
                ) : (
                  <div className="col-md-2 left">
                    <div className="child-menu-section">
                      <AllPropertyReportSidebar
                        property_slug={this.state.property_slug}
                      />
                    </div>
                  </div>
                )}

                <div
                  className={
                    this.props.match.params.propertySlug !== undefined
                      ? "col-md-10 right"
                      : "col-md-12"
                  }
                >
                  <div className="col-md-12 mg-t-10 mg-b-10 pd-t-10 pd-b-10"></div>
                  <div className="col-md-12 mg-t-0 mg-b-10 pd-t-0 pd-b-10 pd-l-15 pd-r-0">
                    <div className="dashbaord-pageHeadertitle d-flex justify-content-between align-items-center flex-wrap mg-b-0">
                      <h3>{this.state.labels.report_title}</h3>
                      <div className="d-flex justify-content-end pd-l-0 pd-r-0 mg-b-0">
                        <button
                          className="btn-success-outline-small mr-3"
                          onClick={this.searchToggle}
                        >
                          Search
                        </button>
                        {global.userPermissions.checkPermission(
                          "wh-revenue-share-report-export",
                        ) && (
                          <button
                            className="btn-success-outline-small mr-3"
                            onClick={this.exportToggle}
                          >
                            Export
                          </button>
                        )}
                        {/* {global.userPermissions.checkPermission(
                          "wh-revenue-share-report-labels-edit",
                        ) && (
                          <button
                            className="btn-success-outline-small mr-0"
                            onClick={this.openLabelsModal}
                          >
                            Edit Labels
                          </button>
                        )} */}
                      </div>
                    </div>
                  </div>

                  {/* Property filter + search applied display */}
                  {this.props.match.params.propertySlug === undefined ? (
                    <>
                      <div className="row pd-lg-l-15 pd-xs-l-15">
                        <div className="col-xxl-2 col-sm-3 pd-lg-r-0 pd-xs-r-15 pd-lg-l-45 pd-xs-l-15">
                          <div className="col-sm-12 mg-t-5 mg-md-t-0 pd-l-0 pd-lg-r-30 pd-xs-r-0 mg-b-20">
                            <label className="form-label text-left pd-b-2 mb-0">
                              Property
                            </label>
                            <Select
                              isMulti
                              styles={{
                                option: (provided, state) => ({
                                  ...provided,
                                  color:
                                    state.isSelected && state.isFocused
                                      ? "white"
                                      : state.isSelected
                                        ? "black"
                                        : state.isFocused && "white",
                                  backgroundColor:
                                    state.isSelected && state.isFocused
                                      ? "black"
                                      : state.isFocused && "black",
                                }),
                              }}
                              className="multiselect property_select"
                              menuPlacement="bottom"
                              placeholder="All Properties"
                              value={this.state.selectedPropertyList}
                              options={this.state.newPropertyList}
                              onChange={(value) =>
                                this.handleChangePropertyList(value)
                              }
                              defaultValue={this.state.selectedPropertyList}
                              theme={(theme) => ({
                                ...theme,
                                borderRadius: 2,
                                colors: {
                                  ...theme.colors,
                                  primary: "#fff",
                                  primary75: "#000",
                                  primary50: "#000",
                                  primary25: "#000",
                                },
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-xxl-10 col-md-9 col-sm-8 pd-r-15 pd-l-15">
                          <div className="col-xxl-12 col-sm-12 pd-r-0 pd-lg-r-30 pd-xs-l-30 d-inline-flex align-items-start justify-content-end justify_mob_filter scrolling-carousel">
                            {this.state.is_search === 1 ? (
                              <div className="custm-filter">
                                <div>
                                  <label className="form-label text-left pd-b-2 mb-0">
                                    Search Applied On
                                  </label>
                                </div>
                                <div className="filter_bg_cust">
                                  {this.state.module_search_display.map(
                                    (item, i) => (
                                      <span key={i}>{item}</span>
                                    ),
                                  )}
                                  <button
                                    onClick={this.clearFilter}
                                    className="btn-fliter-clear"
                                  >
                                    Clear
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="col-xxl-12 col-sm-12 pd-xs-l-30 d-inline-flex align-items-start justify-content-end justify_mob_filter scrolling-carousel">
                                <div className="custm-filter">
                                  <div>
                                    <label className="form-label text-left pd-b-2 mb-0">
                                      Search Applied On
                                    </label>
                                  </div>
                                  <div className="filter_bg_cust">
                                    <span>
                                      {`Period Start Date -  ${Moment(
                                        this.state.start_date,
                                      ).format("MM/DD/YY")} `}
                                    </span>
                                    <span>
                                      {` Period End Date - ${Moment(
                                        this.state.end_date,
                                      ).format("MM/DD/YY")}`}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="col-xxl-12 pd-r-15 pd-l-15">
                      <div className="col-xxl-12 col-sm-12 pd-r-0 pd-lg-r-30 pd-xs-l-30 d-inline-flex align-items-start justify-content-end justify_mob_filter scrolling-carousel">
                        {this.state.is_search === 1 ? (
                          <div className="custm-filter">
                            <div>
                              <label className="form-label text-left pd-b-2 mb-0">
                                Search Applied On
                              </label>
                            </div>
                            <div className="filter_bg_cust">
                              {this.state.module_search_display.map(
                                (item, i) => (
                                  <span key={i}>{item}</span>
                                ),
                              )}
                              <button
                                onClick={this.clearFilter}
                                className="btn-fliter-clear"
                              >
                                Clear
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="col-xxl-12 col-sm-12 pd-xs-l-30 d-inline-flex align-items-start justify-content-end justify_mob_filter scrolling-carousel">
                            <div className="custm-filter">
                              <div>
                                <label className="form-label text-left pd-b-2 mb-0">
                                  Search Applied On
                                </label>
                              </div>
                              <div className="filter_bg_cust">
                                <span>
                                  {`Period Start Date -  ${Moment(
                                    this.state.start_date,
                                  ).format("MM/DD/YY")} `}
                                </span>
                                <span>
                                  {` Period End Date - ${Moment(
                                    this.state.end_date,
                                  ).format("MM/DD/YY")}`}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Summary cards */}

                  <div className="col-md-12 mg-t-0 mg-b-10 pd-t-0 pd-b-10 pd-lg-l-0 pd-lg-r-15 pd-xs-r-0">
                    <div className="scrolling-carousel pd-l-15 scroll-slide">
                      <Tab.Container
                        id="left-tabs-example"
                        defaultActiveKey="all"
                      >
                        <Tab.Content className="m-0">
                          <div className="dashboardRightcard background-none pt-0 pd-l-30 pd-r-30">
                            <div className="mg-t-18 brandList pd-l-15 pd-r-15 payout-report-tbl- resident-payout-table earning-report-tbl payout-details-tbl">
                              <div className="pd-lg-l-0 pd-xs-l-0">
                                <div className="scroll-report-table">
                                  <ReactDatatable
                                    className="table no-wrap mg-b-30 pd-b-2 brandtable border table_last_row"
                                    config={this.state.config}
                                    records={this.state.records}
                                    columns={this.state.columns}
                                    extraButtons={this.state.extraButtons}
                                    dynamic={true}
                                    total_record={this.state.total_record}
                                    onChange={this.tableChangeHandler}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </Tab.Content>
                      </Tab.Container>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </main>

        {/* Search modal */}
        <Modal
          show={this.state.isSearch}
          className="modal-large searchModalbox"
          centered
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              this.filterData();
            }
          }}
        >
          <Modal.Body className="width_402 p-4 border_radius_14">
            <span className="welcomeClose" onClick={() => this.searchToggle()}>
              {global.closee}
            </span>
            <h3 className="text-center">
              Search in {this.state.labels.report_title}
            </h3>
            <div className="row col-12 m-0 pd-lg-l-15 pd-lg-r-15 pd-xs-l-0 pd-xs-r-0">
              <div className="col-lg-6 col-md-6 col-sm-12 pd-lg-l-15 pd-lg-r-15 pd-sm-l-15 pd-sm-r-15 pd-xs-l-0 pd-xs-r-0">
                <InputGroup
                  className="mt-3"
                  ref={this.periodStartDateInputGroupRef}
                >
                  <label>Period Start Date (Checkout)</label>
                  <DatePicker
                    className="form-control max_width_100"
                    showMonthDropdown
                    showYearDropdown
                    scrollableYearDropdown
                    disabledKeyboardNavigation
                    strictParsing
                    autoComplete="off"
                    yearDropdownItemNumber={global.yearDropdownItemNumber}
                    name="start_date"
                    selected={this.state.start_date}
                    dateFormat="MM/dd/yy"
                    onChange={(data) =>
                      this.changeHandler({
                        target: {
                          type: "date",
                          name: "start_date",
                          value: data,
                        },
                      })
                    }
                    popperPlacement={this.state.periodStartDatePlacement}
                    onCalendarOpen={this.checkPeriodStartDatePlacement}
                    popperModifiers={{
                      preventOverflow: {
                        enabled: true,
                        escapeWithReference: false,
                      },
                    }}
                  />
                  {this.state.checkoutFromError && (
                    <span className="field-validation-error">
                      {this.state.checkoutFromError}
                    </span>
                  )}
                </InputGroup>
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 pd-lg-l-15 pd-lg-r-15 pd-sm-l-15 pd-sm-r-15 pd-xs-l-0 pd-xs-r-0">
                <InputGroup
                  className="mt-3"
                  ref={this.periodEndDateInputGroupRef}
                >
                  <label>Period End Date (Checkout)</label>
                  <DatePicker
                    className="form-control max_width_100"
                    showMonthDropdown
                    showYearDropdown
                    scrollableYearDropdown
                    disabledKeyboardNavigation
                    strictParsing
                    autoComplete="off"
                    yearDropdownItemNumber={global.yearDropdownItemNumber}
                    name="end_date"
                    selected={this.state.end_date}
                    dateFormat="MM/dd/yy"
                    onChange={(data) =>
                      this.changeHandler({
                        target: { type: "date", name: "end_date", value: data },
                      })
                    }
                    popperPlacement={this.state.periodEndDatePlacement}
                    onCalendarOpen={this.checkPeriodEndDatePlacement}
                    popperModifiers={{
                      preventOverflow: {
                        enabled: true,
                        escapeWithReference: false,
                      },
                    }}
                  />
                  {this.state.checkoutToError && (
                    <span className="field-validation-error">
                      {this.state.checkoutToError}
                    </span>
                  )}
                </InputGroup>
              </div>
            </div>
            <div className="row col-12 m-0 pd-lg-l-15 pd-lg-r-15 pd-xs-l-0 pd-xs-r-0">
              <div className="col-lg-6 col-md-6 col-sm-12 pd-lg-l-15 pd-lg-r-15 pd-sm-l-15 pd-sm-r-15 pd-xs-l-0 pd-xs-r-0">
                <InputGroup className="mt-3">
                  <label>Unit</label>
                  <div className="col-12 p-0">
                    <Select
                      isMulti
                      styles={{
                        option: (provided, state) => ({
                          ...provided,
                          color:
                            state.isSelected && state.isFocused
                              ? "white"
                              : state.isSelected
                                ? "black"
                                : state.isFocused && "white",
                          backgroundColor:
                            state.isSelected && state.isFocused
                              ? "black"
                              : state.isFocused && "black",
                        }),
                      }}
                      isClearable={true}
                      className="multiselect"
                      menuPlacement="auto"
                      value={this.state.selectedUnitList}
                      options={this.state.newUnitList}
                      onChange={(value) => this.handleChangeUnitList(value)}
                      defaultValue={this.state.selectedUnitList}
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 2,
                        colors: {
                          ...theme.colors,
                          primary: "#fff",
                          primary75: "#000",
                          primary50: "#000",
                          primary25: "#000",
                        },
                      })}
                    />
                  </div>
                </InputGroup>
              </div>
            </div>
            <div className="d-flex justify-content-center mt-3 row">
              <button
                type="button"
                className="m-0 btn btn-outline-light btn-block"
                onClick={() => this.searchToggle()}
              >
                Cancel
              </button>
              <button
                type="button"
                className="m-0 btn btn btn-success btn-block"
                onClick={() => this.filterData()}
                disabled={this.state.isSubmit ? true : false}
              >
                {this.state.isSubmit ? global.loader : "Search"}
              </button>
            </div>
          </Modal.Body>
        </Modal>

        {/* Edit Labels modal — rename any column/group header without a code change */}
        <Modal
          show={this.state.isLabelsModalOpen}
          className="modal-large searchModalbox"
          centered
        >
          <Modal.Body className="width_402 p-4 border_radius_14 wh-labels-modal-body">
            <span className="welcomeClose" onClick={this.closeLabelsModal}>
              {global.closee}
            </span>
            <h3 className="text-center">Edit Report Labels</h3>
            <div className="row col-12 m-0 pd-lg-l-15 pd-lg-r-15 pd-xs-l-0 pd-xs-r-0 wh-labels-list">
              {Object.keys(DEFAULT_LABELS).map((columnKey) => (
                <div
                  key={columnKey}
                  className="col-lg-6 col-md-6 col-sm-12 mt-2"
                >
                  <label className="wh-label-input-label">
                    {DEFAULT_LABELS[columnKey]}
                  </label>
                  <div className="d-flex align-items-center">
                    <input
                      type="text"
                      className="form-control wh-label-input"
                      value={this.state.editLabels[columnKey] || ""}
                      onChange={(e) =>
                        this.changeLabelInput(columnKey, e.target.value)
                      }
                    />
                    <button
                      type="button"
                      className="btn-fliter-clear ml-2 wh-label-reset-btn"
                      onClick={() => this.resetLabelInput(columnKey)}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-center mt-3 row">
              <button
                type="button"
                className="m-0 btn btn-outline-light btn-block"
                onClick={this.closeLabelsModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="m-0 btn btn btn-success btn-block"
                onClick={this.saveLabels}
                disabled={this.state.isLabelsSaving}
              >
                {this.state.isLabelsSaving ? global.loader : "Save"}
              </button>
            </div>
          </Modal.Body>
        </Modal>

        {/* Export modal — xlsx/csv, mirrors Resident Payout Report's export UI */}
        <Modal
          show={this.state.isExportModalOpen}
          className="modal-xs"
          centered
        >
          <Modal.Body className="width_402 p-4 border_radius_14">
            <span className="welcomeClose" onClick={() => this.exportToggle()}>
              {global.closee}
            </span>
            <h3 className="text-center">Export</h3>
            <div className="row col-12 m-0">
              <div className="col-lg-12 col-md-12 col-sm-12 p-0">
                <InputGroup className="mt-0">
                  <label>Select Export File Type : </label>
                  <div className="row">
                    <div className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mg-t-5 mg-md-t-0 pd-lg-l-30 pd-lg-r-30 pd-xs-l-15 pd-xs-r-15 mg-b-15">
                      <Form.Check
                        onChange={this.changeExportFileType}
                        name="export_file_type"
                        value="xlsx"
                        checked={this.state.exportFileType === "xlsx"}
                        type="radio"
                        id="wh_export_xlsx"
                        label="XLSX"
                        className="rdiobox mg-r-20"
                      />
                    </div>
                    <div className="col-sm-12 col-md-6 col-lg-6 col-xl-6 mg-t-5 mg-md-t-0 pd-lg-l-30 pd-lg-r-30 pd-xs-l-15 pd-xs-r-15 mg-b-15">
                      <Form.Check
                        onChange={this.changeExportFileType}
                        name="export_file_type"
                        value="csv"
                        checked={this.state.exportFileType === "csv"}
                        type="radio"
                        id="wh_export_csv"
                        label="CSV"
                        className="rdiobox mg-r-20"
                      />
                    </div>
                  </div>
                </InputGroup>

                {this.state.exportMessage !== "" && (
                  <div>{this.state.exportMessage}</div>
                )}

                <div className="d-flex justify-content-center mt-3">
                  <button
                    type="button"
                    className="mx-auto- mg-r-30 btn btn btn-success btn-block"
                    onClick={this.exportReport}
                    disabled={this.state.isExporting}
                  >
                    {this.state.isExporting ? global.loader : "Export"}
                  </button>
                  <button
                    type="button"
                    className="mx-auto- m-0 btn btn-outline-light btn-block"
                    onClick={() => this.exportToggle()}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </>
    );
  }
}

export default WHRevenueShareReport;
